import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Copy, BookOpen, Activity, Layers, History, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, usd } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { BookFormDialog } from "../components/BookFormDialog";
import { DuplicateDialog } from "../components/DuplicateDialog";
import { Button } from "../components/ui/button";
import { ProgressSummary } from "../components/book/ProgressBars";
import { FirstProjectGuide } from "../components/guide/FirstProjectGuide";

const EDITION_LABEL = { ebook: "Ebook", paperback: "Paperback", hardcover: "Hardcover", audiobook: "Audio" };

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="panel p-5" data-testid={testId}>
      <Icon size={16} className="text-amber-500 mb-3" />
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function BookCard({ b, onDuplicate, progress }) {
  const navigate = useNavigate();
  return (
    <div className="panel p-6 hover:border-slate-700 transition-colors flex flex-col fade-up" data-testid={`book-card-${b.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          {["comic", "manga", "webtoon", "poetry"].includes(b.format) && <p className="text-[10px] uppercase tracking-wider text-amber-300 mb-1" data-testid={`book-format-${b.id}`}>{{ comic: "Comic", manga: "Manga", webtoon: "Webtoon", poetry: "Poetry" }[b.format]}</p>}
          {b.series_name && <p className="eyebrow mb-2 truncate">{b.series_name}{b.book_number != null ? ` · Book ${b.book_number}` : ""}</p>}
          <Link to={`/app/books/${b.id}`} className="font-display text-xl text-slate-100 hover:text-amber-300 block truncate" data-testid={`book-open-${b.id}`}>{b.title}</Link>
          {b.subtitle && <p className="text-sm text-slate-400 truncate">{b.subtitle}</p>}
        </div>
        <button onClick={() => onDuplicate(b)} className="text-slate-500 hover:text-amber-300" title="Duplicate" data-testid={`book-duplicate-${b.id}`}><Copy size={16} /></button>
      </div>
      <div className="text-sm text-slate-400 space-y-1 mb-4">
        <p data-testid={`book-version-${b.id}`}>{["comic", "manga", "webtoon", "poetry"].includes(b.format) ? "Open to keep building" : b.current_version ? `v${b.current_version.number} · ${b.current_version.word_count.toLocaleString()} words · ${b.current_version.chapter_count} sections` : "No manuscript yet"}</p>
        <p className="text-xs text-slate-500 truncate">{b.last_activity} · {fmtDate(b.updated_at)}</p>
      </div>
      <ProgressSummary p={progress} bookId={b.id} onGo={(t) => navigate(`/app/books/${b.id}?tab=${t}`)} />
      <div className="flex flex-wrap gap-1.5 mt-auto">
        {Object.entries(b.editions || {}).map(([k, e]) => <StatusBadge key={k} status={e.outdated ? "outdated" : e.status} label={`${EDITION_LABEL[k]}: ${e.outdated ? "outdated" : e.status.replaceAll("_", " ")}`} />)}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [books, setBooks] = useState(null);
  const [progress, setProgress] = useState({});
  const [stats, setStats] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dup, setDup] = useState(null);
  const navigate = useNavigate();
  const load = () => {
    api.get("/books").then((r) => setBooks(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/books-progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/dashboard").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="eyebrow mb-2">Library</p>
          <h1 className="font-display text-4xl sm:text-5xl">My Books</h1>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-500" data-testid="new-book-button"><Plus size={16} className="mr-1" />New book</Button>
      </div>
      <FirstProjectGuide />
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <Stat icon={BookOpen} label="Books" value={stats.books} testId="stat-books" />
          <Stat icon={Layers} label="Series" value={stats.series} testId="stat-series" />
          <Stat icon={History} label="Saved versions" value={stats.versions} testId="stat-versions" />
          <Stat icon={Activity} label="Jobs running" value={stats.running_jobs} testId="stat-jobs" />
          <Stat icon={DollarSign} label="AI spend this month" value={usd(stats.month_spend_usd)} testId="stat-spend" />
        </div>
      )}
      {books === null ? <p className="text-slate-500">Loading…</p> : books.length === 0 ? (
        <div className="panel p-12 text-center" data-testid="empty-library">
          <BookOpen className="mx-auto text-amber-500 mb-4" />
          <h3 className="font-display text-2xl mb-2">Start your first book</h3>
          <p className="text-slate-400 text-sm mb-6">Create a project, then upload a DOCX, EPUB, PDF, RTF or TXT manuscript.</p>
          <Button onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-500" data-testid="empty-new-book-button">Create book</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 [&>*]:min-w-0" data-testid="book-grid">
          {books.map((b) => <BookCard key={b.id} b={b} onDuplicate={setDup} progress={progress[b.id]} />)}
        </div>
      )}
      {stats?.activity?.length > 0 && (
        <div className="mt-12">
          <p className="eyebrow mb-4">Recent activity</p>
          <div className="panel divide-y divide-slate-800" data-testid="recent-activity">
            {stats.activity.map((a) => (
              <div key={a.id} className="px-5 py-3 flex justify-between gap-4 text-sm">
                <span className="text-slate-300 truncate"><span className="font-mono text-xs text-amber-400/80 mr-2">{a.action}</span>{a.target}</span>
                <span className="text-xs text-slate-500 shrink-0">{fmtDate(a.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <BookFormDialog open={creating} onOpenChange={setCreating} onSaved={(b) => navigate(`/app/books/${b.id}?tab=upload`)} />
      <DuplicateDialog book={dup} onClose={() => setDup(null)} onDone={load} />
    </div>
  );
}
